<template>
  <div :class="['selector', { opacity: opacity }]">
    <div
      v-click-outside="onClickOutside"
      class="selector__wrapper"
    >
      <div class="selector__left">
        <div
          v-if="opacity"
          class="selector__search-icon"
        >
          <search-icon />
        </div>
        <p
          v-if="label"
          class="selector__label"
        >
          {{ label }}<span v-if="required">*</span>
        </p>
      </div>
      <div
        v-if="opacity"
        :class="['selector__dropdown-icon', { 'selector__dropdown-icon--active': showOptions }]"
      >
        <dropdown-icon />
      </div>
      <input
        :value="searchQuery"
        class="selector__input"
        :placeholder="placeholder"
        type="text"
        @focus="openOptions"
        @input="
          searchQuery = $event.target.value;
          $emit('search', searchQuery);
        "
      >
      <div
        v-if="showOptions"
        class="selector__options"
      >
        <div
          v-if="!optionsFiltered.length"
          class="selector__option"
        >
          Результатов с таким названием нет
        </div>
        <template v-else>
          <div
            v-for="item in optionsFiltered"
            :key="optionKey ? item[optionKey] : item"
            class="selector__option"
            :class="{
              'selector__option--active': isEqual(item, value),
              'selector__option--disabled': optionDisabled(item)
            }"
            @click="setOption(item)"
          >
            {{ optionText ? item[optionText] : item }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { isEqual } from 'lodash';
import vClickOutside from 'v-click-outside';
import SearchIcon from '@/components/Icons/SearchIcon';
import DropdownIcon from '@/components/Icons/DropdownIcon';

export default {
  name: 'Select',
  directives: { clickOutside: vClickOutside.directive },
  components: {
    SearchIcon,
    DropdownIcon
  },
  props: {
    /**
     * Плейсхолдер для строки поиска
     */
    placeholder: {
      type: String,
      required: true
    },
    /**
     * Массив опций для селектора, может принимать типы, соответствующие value
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Ключ для поля, которое будет показываться в дропдауне опций, если не указано, то используется само value
     */
    optionText: {
      type: String,
      default: undefined
    },
    /**
     * Ключевое поле, нужно для v-for, если не указано, то используется само value
     */
    optionKey: {
      type: String,
      default: undefined
    },
    /**
     * Функция для определения disabled опций, функция принимает в себя item - опцию и должна возвращать true/false
     */
    optionDisabled: {
      type: Function,
      default: () => false
    },
    /**
     * Значение селектора, может быть как одним из примитивов, так и просто объектом
     */
    value: {
      type: [String, Number, Boolean, Date, Object],
      default: undefined
    },
    /**
     * Заголовок селекта
     */
    label: {
      type: String,
      default: undefined
    },
    /**
     * Флаг для показа required-звездочки
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Флаг скрытия иконки поиска
     */
    opacity: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      searchQuery: '',
      showOptions: false
    };
  },
  computed: {
    optionsFiltered() {
      return (
        this.options?.filter(option => {
          return this.optionText
            ? new RegExp(this.searchQuery, 'i').test(option[this.optionText])
            : new RegExp(this.searchQuery, 'i').test(String(option));
        }) ?? []
      );
    }
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.setQuery(value);
      }
    }
  },
  created() {
    if (this.value) {
      this.searchQuery = this.optionText ? this.value[this.optionText] : this.value;
    }
  },
  methods: {
    openOptions() {
      if (this.showOptions) {
        this.closeOptions();
      } else {
        this.showOptions = true;
        this.searchQuery = '';
      }
    },
    closeOptions() {
      this.showOptions = false;
      this.setQuery(this.value);
    },
    onClickOutside() {
      this.closeOptions();
    },
    setOption(item) {
      if (!this.optionDisabled(item)) {
        this.$emit('change', item);
        this.$emit('input', item);
        this.closeOptions();
      }
    },
    setQuery(value) {
      if (value) {
        this.searchQuery = this.optionText ? value[this.optionText] : value;
      } else {
        this.searchQuery = '';
      }
    },
    isEqual(left, right) {
      if (this.optionKey) {
        return left[this.optionKey] === right[this.optionKey];
      } else {
        return isEqual(left, right);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

input::placeholder {
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.3px;
  color: #76767b;
}

.opacity.selector {
  padding-top: 21px;

  .selector__input {
    background-color: #f7f7fb;
    padding: 16px 40px 14px 40px;
    text-overflow: ellipsis;
  }
}

.selector {
  position: relative;
  -moz-user-select: none;
  -khtml-user-select: none;
  user-select: none;
  margin-bottom: 16px;

  &__input {
    position: initial;
    padding: 14px 16px;
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    display: flex;
    justify-content: space-between;
    border-radius: 4px;
    cursor: pointer;
    @include uiFont;
    @extend %text-400-14;
    z-index: 1;
  }

  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
  }

  &__left,
  &__dropdown-icon {
    position: absolute;
    z-index: 2;
  }

  &__left {
    height: 100%;
    display: flex;
    align-items: center;
    left: 0;
  }

  &__search-icon {
    margin-left: 14px;
    cursor: pointer;
  }

  &__dropdown-icon {
    right: 18px;
    cursor: pointer;
    transition: 0.3s;

    &--active {
      transform: rotate(180deg);
    }
  }

  &__label {
    position: absolute;
    left: 0;
    top: -5px;
    transform: translateY(-100%);
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.3px;
    color: #ababba;

    span {
      color: $ui-red;
    }
  }

  &__options {
    position: absolute;
    left: 0;
    top: calc(100% + 5px);
    width: 100%;
    padding: 20px 16px;
    background-color: #fff;
    box-shadow: 0 4px 10px rgba(43, 45, 52, 0.12);
    border-radius: 4px;
    max-height: 126px;
    overflow-y: auto;
    z-index: 100;
  }

  &__option {
    cursor: pointer;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.3px;
    text-align: initial;

    &--disabled {
      color: $ui-gray-less-contrast;
      cursor: unset;
    }

    &:hover:not(&--disabled) {
      color: $ui-blue-accident;
    }

    &--active {
      color: $ui-blue-accident;
    }
  }

  &__option:not(:last-child) {
    margin-bottom: 16px;
  }
}
</style>
